import {
  ClientEvents,
  LastTrackerPosition,
  TrackerPosition,
} from "@/interfaces";
import api from "@/lib/axios";
import type { EngineDataLevel } from "@/lib/engine-data-level";

interface MapReportRequest {
  start_date: string;
  end_date: string;
  start_hour?: string;
  end_hour?: string;
  vehicle_id: number;
}

interface PositionsReportRequest {
  start_date: string;
  end_date: string;
  vehicle_id: number;
  start_hour: string;
  end_hour: string;
}

export interface StopGroup {
  center_latitude: number;
  center_longitude: number;
  radius: number;
  stops: StopData[];
}

interface MapReportResponse {
  errors: boolean;
  data: {
    totals: {
      distance: number;
    };
    events_data: {
      [key: string]: ClientEvents[];
    };
    drivers_colors: string[];
    vehicle_title: string;
    vehicle_plate: string;
  } & {
    [key: string]: {
      stop_groups: StopGroup[];
      drivers_with_colors: Array<{
        name: string;
        color: string;
      }>;
      totals: {
        events_count: {
          over_speed: number;
        };
        timings: Timings;
        distance: number;
        max_speed: number;
        speed_average: number;
        average_speed: number;
        extra_km: number;
        fuel_expense: number;
      };
      drivers_list: string[];
      first_movement: string;
      last_movement: string;
      positions: TrackerPosition[];
      speed_chart: {
        hour: string;
        speed: number;
      }[];
      last_positions: LastTrackerPosition;
    };
  };
}

export interface Travel {
  average_speed: number;
  distance: number;
  drivers: string[];
  driver_color_hex: string;
  drivers_with_colors: Array<{
    name: string;
    color: string;
  }>;
  end: string;
  end_address?: {
    display_name?: string | null;
    address?: {
      city?: string;
      state?: string;
      country?: string;
    } | null;
    lat?: string | null;
    lon?: string | null;
  } | null;
  ignition: number;
  last_latitude: number;
  last_longitude: number;
  max_speed: {
    date: string;
    lat: number;
    long: number;
    value: number;
  };
  over_speed_count: number;
  over_speed_time: string;
  speed_average: number;
  start: string;
  start_address?: {
    display_name?: string | null;
    address?: {
      city?: string;
      state?: string;
      country?: string;
    } | null;
    lat?: string | null;
    lon?: string | null;
  } | null;
  time: string;
  positions: TrackerPosition[];
  date: string;
}

interface TravelReportRequest {
  vehicle_id: number;
  start_hour: string;
  end_hour: string;
  start_date: string;
  end_date: string;
}

export interface DayTotals {
  speed_average: number;
  average_speed: number;
  time: string;
  distance: number;
  over_speed_count: number;
  over_speed_time: string;
  max_speed: number;
  avarage_time_between_travels: string;
  avarage_distance_between_travels: number;
  avarage_time_to_travel_one_km: string;
}

interface TravelReportResponse {
  errors: boolean;
  data: {
    totals: {
      avarage_distance_between_travels: number;
      avarage_time_between_travels: string;
      avarage_time_to_travel_one_km: string;
      average_speed: number;
      distance: number;
      max_speed: number;
      over_speed_count: number;
      over_speed_time: string;
      speed_average: number;
      time: string;
    };
    day_totals: Record<string, DayTotals>;
    travels: Travel[];
    vehicle_title: string;
  };
}

interface RpmTimings {
  green: number;
  yellow: number;
  red: number;
  idle: number;
}

export interface Timings {
  movement: number;
  engine_on: number;
  stopped_with_ignition_on: number;
  off: number;
  over_speed: number;
  rpm?: RpmTimings;
  /**
   * Motor ligado e parado, medido pelo fio de entrada do rastreador. Só existe
   * para veículos de nível `engine_wire` e para períodos posteriores à virada
   * da Fase 2 — ausente não é zero.
   */
  engine_idle?: number;
}

export interface StopData {
  latitude: number;
  longitude: number;
  interval: string;
  ignition: number;
  speed: number;
  start_date: string;
}

export interface StopGroup {
  center_latitude: number;
  center_longitude: number;
  radius: number;
  stops: StopData[];
}

export interface ChartDataPoint {
  hour?: string;
  speed?: number;
  rpm?: number | null;
  is_stop?: boolean;
  stop_time?: string;
  label?: string;
  start_hour?: string;
  end_hour?: string;
}

export interface VehicleJourneyData {
  date?: string;
  first_move: string;
  last_move: string;
  timings: Timings;
  distance: number;
  out_work_distance: number;
  max_speed: number;
  chart_data: ChartDataPoint[];
}

export interface VehicleJourneyReportTotals {
  over_speed_count: number;
  timings: Timings;
  distance: number;
  out_work_distance: number;
  max_speed: number;
  stops_count: number;
  consumption: number;
}

interface VehicleJourneyReportRequest {
  start_date: string;
  end_date: string;
  vehicle_id: number;
}

interface BaseVehicleJourneyReportData {
  vehicle_title: string;
  vehicle_plate: string;
  has_engine_on_info: boolean;
  /** Ausente em API anterior à Fase 1; derivar de `has_engine_on_info`. */
  engine_data_level?: EngineDataLevel;
  idle_is_estimated?: boolean;
  totals: VehicleJourneyReportTotals;
}

export type VehicleJourneyDataByDays = {
  [dateKey: string]: VehicleJourneyData;
};

// Combine the two types
type VehicleJourneyReportResponse = {
  errors: boolean;
  data: BaseVehicleJourneyReportData & VehicleJourneyDataByDays;
};

export interface VehicleInOutMapping {
  id: string;
  label: string;
  inverse: number;
  states: string[];
}

interface PositionsReportResponse {
  errors: boolean;
  data: {
    positions: {
      [key: string]: {
        stop_groups: StopGroup[];
        drivers_with_colors: Array<{
          name: string;
          color: string;
        }>;
        totals: {
          events_count: {
            over_speed: number;
          };
          timings: Timings;
          distance: number;
          max_speed: number;
          speed_average: number;
          average_speed: number;
          extra_km: number;
          fuel_expense: number;
        };
        drivers_list: string[];
        first_movement: string;
        last_movement: string;
        positions: TrackerPosition[];
      };
    };
    in_out_mapping: VehicleInOutMapping[];
    vehicle_title: string;
  };
}

interface VisitsReportRequest {
  filter: "vehicle" | "reference_point";
  start_date: string;
  end_date: string;
  vehicle_id?: number;
  user_id?: number;
  reference_point_id?: number;
  separation?: "date" | "reference_point";
  count_all_daily_visits?: boolean;
}

export interface VisitsAnalysisData {
  chart_data: [
    {
      label: string;
      count: number;
      time: string;
    }[],
    {
      label: string;
      average_interval: number;
      longest_interval: number;
    }[],
  ];
  analysis_data: {
    total_visits: number;
    total_clients: number;
    average_visits_per_day: number;
    average_standing_time: string;
    km_travelled_between_visit: number;
    time_between_visit: string;
  };
}

export interface Visit {
  reference_point?: string;
  reference_point_id?: number;
  vehicle_title?: string;
  vehicle_plate?: string;
  visit_objective_id?: number | null;
  visit_objective_title?: string | null;
  date: string;
  date_sql: string;
  entrance_hour: string;
  exit_hour: string;
  standing_time: string;
}

export interface VisitsObjectiveSummary {
  objective_id: number | null;
  objective_title: string;
  visits_count: number;
}

export interface VisitsReportData {
  reference_point?: string;
  vehicle_title?: string;
  vehicle_plate?: string;
  analysis: VisitsAnalysisData | null;
  dates: {
    [date: string]: {
      title: string;
      visits: Visit[];
    };
  };
  reference_points: {
    [id: number]: {
      title: string;
      visits: Visit[];
    };
  };
  objectives_summary?: VisitsObjectiveSummary[];
}

interface VisitsReportResponse {
  errors: boolean;
  data: VisitsReportData;
}

export interface VisitsHeatmapRequest {
  vehicle_id: number;
  start_date: string;
  end_date: string;
}

export interface HeatmapReferencePoint {
  id: number;
  name: string;
  latitude: number;
  longitude: number;
  radius: number;
  visits_count: number;
  color: string;
  color_index: number;
}

export interface VisitsHeatmapData {
  reference_points: HeatmapReferencePoint[];
  color_scale: string[];
  min_visits: number;
  max_visits: number;
  total_points: number;
}

export interface VisitsHeatmapResponse {
  errors: boolean;
  data: VisitsHeatmapData;
}

interface TachographReportRequest {
  start_date: string;
  end_date: string;
  vehicle_id: number;
}

export const trackReportsService = {
  getMapReport: async (params: MapReportRequest) => {
    const response = await api.get<MapReportResponse>("map_report", { params });
    return response.data;
  },

  getPositionsReport: async (params: PositionsReportRequest) => {
    const response = await api.get<PositionsReportResponse>(
      "positions_report",
      { params },
    );
    return response.data;
  },

  getPositionsReportPdf: async (params: PositionsReportRequest) => {
    const response = await api.get("positions_report/pdf", {
      params,
      responseType: "blob",
    });
    return response.data;
  },

  getTravelReport: async (params: TravelReportRequest) => {
    const response = await api.get<TravelReportResponse>("travel_report", {
      params,
    });
    return response.data;
  },

  getTravelReportPdf: async (params: TravelReportRequest) => {
    const response = await api.get("travel_report/pdf", {
      params,
      responseType: "blob",
    });
    return response.data;
  },

  getVehicleJourneyReport: async (params: VehicleJourneyReportRequest) => {
    const response = await api.get<VehicleJourneyReportResponse>(
      "vehicle_journey_report",
      {
        params,
      },
    );
    return response.data;
  },

  getVehicleJourneyReportPdf: async (params: VehicleJourneyReportRequest) => {
    const response = await api.get("vehicle_journey_report/pdf", {
      params,
      responseType: "blob",
    });
    return response.data;
  },

  getVisitsReport: async (params: VisitsReportRequest) => {
    const response = await api.get<VisitsReportResponse>("visits_report", {
      params,
    });
    return response.data;
  },

  getVisitsReportPdf: async (params: VisitsReportRequest) => {
    const response = await api.get("visits_report/pdf", {
      params,
      responseType: "blob",
    });
    return response.data;
  },

  getVisitsHeatmap: async (params: VisitsHeatmapRequest) => {
    const response = await api.get<VisitsHeatmapResponse>(
      "visits_heatmap_report",
      { params },
    );
    return response.data;
  },

  getTacographReportPdf: async (params: TachographReportRequest) => {
    const response = await api.get("tachograph_report/pdf", {
      params,
      responseType: "blob",
    });
    return response.data;
  },
};
